import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Plus, RotateCcw, Search,
  ChevronLeft, ChevronRight, Eye,
} from 'lucide-react'
import { PageHeader } from '../../components/ui/PageHeader'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { useCurrency } from '../../hooks/useCurrency'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import { NotAuthorizedView } from '../../components/permissions/NotAuthorizedView'
import { useDebounce } from '../../hooks/useDebounce'
import { supabase } from '../../lib/supabase'
import { displayDate } from '../../utils/dates'
import { cn } from '../../utils/cn'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

type ReturnRow = {
  id: string
  return_number: string
  original_sale_id: string
  customer_name: string
  return_date: string
  reason: string
  return_type: string
  total_amount: number
  status: string
  created_at: string
  sales_orders: { order_number: string } | null
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'badge-orange',
  approved: 'badge-green',
  rejected: 'badge-red',
  completed: 'badge-blue',
}

const TYPE_LABEL: Record<string, string> = {
  refund: 'Cash Refund',
  customer_account: 'Customer Account',
  credit_note: 'Customer Credit',
  reduce_ar: 'Reduce AR',
}

const PAGE_SIZE = 20

export default function SalesReturnsPage() {
  const { format } = useCurrency()
  const { tenant, user } = useAuthStore()
  const { viewScope } = usePermissions()
  const scope = viewScope('sales')
  const ownerId = scope === 'own' ? (user?.id ?? '') : undefined
  const tid = tenant?.id ?? ''
  const navigate = useNavigate()

  // List state
  const [search, setSearch] = useState('')
  const dSearch = useDebounce(search, 300)
  const [page, setPage] = useState(0)

  // ── List query ──────────────────────────────────────────────────────────────

  const { data: returns_ = [], isLoading } = useQuery({
    queryKey: ['sale-returns', tid, dSearch, page, ownerId],
    enabled: !!tid && scope !== 'none',
    queryFn: async () => {
      let q = db('sale_returns')
        .select('*, sales_orders!sale_returns_original_sale_id_fkey(order_number)')
        .eq('tenant_id', tid)
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)
      if (ownerId) q = q.eq('created_by', ownerId)
      if (dSearch) q = q.ilike('customer_name', `%${dSearch}%`)
      const { data, error } = await q
      if (error) throw error
      return (data ?? []) as ReturnRow[]
    },
  })

  // ── Render ──────────────────────────────────────────────────────────────────

  if (scope === 'none') return <NotAuthorizedView />

  return (
    <div className="space-y-5">
      <PageHeader
        title="Sales Returns"
        subtitle="Process refunds and returns from customers"
        actions={
          <button className="btn-primary flex items-center gap-2" onClick={() => navigate('/sales/returns/new')}>
            <Plus className="h-4 w-4" /> New Return
          </button>
        }
      />

      {/* Search */}
      <div className="card p-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            className="input pl-9"
            placeholder="Search by customer name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Returns table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex justify-center"><Spinner /></div>
        ) : returns_.length === 0 ? (
          <EmptyState
            icon={<RotateCcw className="h-10 w-10 text-gray-300" />}
            title="No returns yet"
            description="Process sales returns from the button above."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Return #', 'Invoice #', 'Customer', 'Date', 'Amount', 'Type', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {returns_.map(r => (
                    <tr
                      key={r.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/sales/returns/${r.id}`)}
                    >
                      <td className="px-4 py-3 font-mono font-medium text-brand-600">{r.return_number}</td>
                      <td className="px-4 py-3 font-mono text-gray-600">{r.sales_orders?.order_number ?? '—'}</td>
                      <td className="px-4 py-3 font-medium">{r.customer_name}</td>
                      <td className="px-4 py-3 text-gray-500">{displayDate(r.return_date)}</td>
                      <td className="px-4 py-3 font-semibold">{format(r.total_amount)}</td>
                      <td className="px-4 py-3"><span className="badge badge-blue">{TYPE_LABEL[r.return_type] ?? r.return_type}</span></td>
                      <td className="px-4 py-3"><span className={cn('badge', STATUS_BADGE[r.status] ?? 'badge-gray')}>{r.status}</span></td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/sales/returns/${r.id}`) }}
                          className="p-1 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg"
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">Page {page + 1}</span>
              <div className="flex gap-2">
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="btn-secondary p-1.5 disabled:opacity-40">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button disabled={returns_.length < PAGE_SIZE} onClick={() => setPage(p => p + 1)} className="btn-secondary p-1.5 disabled:opacity-40">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

    </div>
  )
}
